import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchAccommodationGroupDetail } from '../services/accommodationApi';

/**
 * Custom hook to fetch accommodation group details
 * Can be used across all pages (mobile, desktop, checkout, etc.)
 *
 * Automatically reads hotel_id from URL parameters (supports both hotel_id and accommodationid)
 *
 * @param {string} defaultHotelId - Default hotel ID if not in URL (optional)
 * @returns {Object} { accommodationGroupDetail, isLoadingGroup, error }
 */
export const useAccommodationGroupDetail = (defaultHotelId = null) => {
  const [searchParams] = useSearchParams();
  const [accommodationGroupDetail, setAccommodationGroupDetail] = useState(null);
  const [isLoadingGroup, setIsLoadingGroup] = useState(false);
  const [error, setError] = useState(null);

  // Only extract hotel_id - ignore other param changes (currency, language, etc.)
  const hotelId = searchParams.get('hotel_id') ||
                  searchParams.get('accommodationid') ||
                  defaultHotelId;

  useEffect(() => {
    const fetchGroupDetail = async () => {
      if (hotelId) {
        setIsLoadingGroup(true);
        setError(null);
        try {
          const groupDetail = await fetchAccommodationGroupDetail(hotelId);
          setAccommodationGroupDetail(groupDetail);
        } catch (err) {
          setError(err.message || 'Failed to fetch accommodation group details');
          // Don't show error to user, just use null
          setAccommodationGroupDetail(null);
        } finally {
          setIsLoadingGroup(false);
        }
      } else {
        // No hotel ID available, reset state
        setAccommodationGroupDetail(null);
        setIsLoadingGroup(false);
      }
    };

    fetchGroupDetail();
  }, [hotelId]);

  return {
    accommodationGroupDetail,
    isLoadingGroup,
    error
  };
};
